import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type { AvatarAppearance } from '../types/index.js';

/** 'replaced' : campus ouvert depuis une autre fenêtre — écran « Revenir ici » (JWT conservé). */
export type AuthState = 'unauthenticated' | 'authenticated' | 'lobby' | 'in-game' | 'replaced';
export type RealtimeStatus = 'connected' | 'reconnecting' | 'failed';

export interface NearbyPlayer {
  userId: string;
  displayName: string;
  distance: number;
}

export interface ProximityMessage {
  /** Présent dans une salle de réunion (chat de la séance). */
  id?: string;
  senderId: string;
  displayName: string;
  text: string;
  timestamp: number;
  isSelf?: boolean;
}

export type ModalId =
  | 'livekit' | 'chat' | 'settings' | 'zone-info'
  | 'broadcast-incoming' | 'kicked'
  | null;

export type UserStatus = 'available' | 'away' | 'busy' | 'focus';

export interface ZonePlayer {
  userId: string;
  displayName: string;
  status: UserStatus;
}

export interface PersonalDeskUi {
  /** Espace bureaux où se tient l'avatar. */
  area: { id: string; name: string } | null;
  /** Bureau personnel où se tient l'avatar. */
  desk: { id: string; ownerName: string; isMine: boolean } | null;
  /** Emplacement libre où se tient l'avatar (tasks/emplacements-bureaux-plan.md). */
  slot: { id: string } | null;
  /** L'espace où se tient l'avatar a encore au moins un emplacement libre. */
  areaHasFreeSlot: boolean;
  /** Mode « poser son bureau » en cours (valid = emplacement possible). */
  placing: { valid: boolean } | null;
  /** Bureau du joueur sur tout le campus (null = aucun). */
  myDesk: { id: string; mapId: string } | null;
  /** Mode « Personnaliser » en cours (DeskCustomizePanel). */
  customizing: {
    /** Objets autorisés (catalogue du tenant). */
    furnitureIds: string[];
    deskWidth: number;
    deskHeight: number;
    selectedId: string | null;
    /** Un objet posé a été ramassé pour être déplacé. */
    carrying: boolean;
    count: number;
    /** Objets posés retirés du catalogue depuis (à enlever pour enregistrer). */
    notAllowed: number;
    saving: boolean;
  } | null;
}

export interface WaveNotification {
  fromUserId: string;
  fromDisplayName: string;
  timestamp: number;
}

export interface GameState {
  // ── Zone
  currentZoneId:   string | null;
  currentZoneName: string | null;
  isTransitioning: boolean;

  // ── Présence
  nearbyPlayers: Map<string, NearbyPlayer>;
  /** Tous les joueurs actuellement dans la zone (peuplé par Colyseus room:state/join/leave) */
  zonePlayers:      Map<string, ZonePlayer>;
  /**
   * Nombre de joueurs avec qui le joueur local peut communiquer en ce moment :
   *   - en bulle fixe  → joueurs dans la même bubbleId
   *   - hors bulle     → joueurs dans le rayon de proximité (nearbyUserIds Colyseus)
   * Mis à jour par GameBridge sur bubble:changed et proximity:changed.
   */
  proximityCount: number;

  // ── Bulles (bubbleId du joueur local + map complète)
  localBubbleId:   string | null;
  /** Type de la bulle locale ('bubble' | 'meeting-room' | ...), tel que reçu
   *  dans bubble:changed — null hors bulle. Sert à distinguer une meeting-room
   *  d'une bulle normale côté UI (ex: bouton d'enregistrement), sans dupliquer
   *  le calcul serveur (never trust the client, cf. ZoneRoom.handleMove). */
  localBubbleType: string | null;
  playerBubbleIds: Map<string, string | null>;
  /** id (map_zones.id) → nom lisible, bulles DB uniquement (voir
   *  ZoneScene.loadZonesFromDB) — permet au HUD d'afficher un nom plutôt que
   *  l'UUID brut pour une bulle créée via l'éditeur (bug trouvé 2026-08-23,
   *  BubleIndicator retombait sur le bubbleId faute de mapping). Les bulles
   *  TMJ n'ont pas besoin de cette table (id === name déjà lisible). */
  bubbleNames: Map<string, string>;
  // Bulle actuelle liée à au moins une porte — verrouiller une bulle sans
  // porte n'a pas de sens (pas de mur, personne ne peut "frapper"), le
  // cadenas ne doit s'afficher que si une porte existe. Calculé dans
  // ZoneScene (seul endroit qui connaît doorZones) sur bubble:changed.
  localBubbleHasDoor: boolean;

  // ── LiveKit
  liveKitConnected: boolean;

  // ── Connexion temps réel (Colyseus) : 'reconnecting' tant que la reconnexion
  // automatique essaie, 'failed' après ~1 min d'échecs (bouton Recharger).
  realtimeStatus: RealtimeStatus;
  activeSpeakers:   string[];
  isLocalMuted:     boolean;

  // ── Broadcast
  isBroadcastActive: boolean;

  // ── Modération
  mutedUntil: number | null; // timestamp ms, null = pas muté

  // ── Joueurs à proximité (userIds bruts de proximity:changed)
  proximityUserIds: string[];

  // ── Chat proximity (tampon court, 20 messages max)
  proximityMessages: ProximityMessage[];

  // ── UI
  activeModal: ModalId;

  // ── Auth (JWT pour les appels fetch depuis GameBridge)
  jwt: string | null;

  // ── Auth state & profil
  authState:       AuthState;
  displayName:     string | null;
  avatarIndex:     number;
  appearance:      AvatarAppearance | null;

  // ── Média local
  isLocalCameraOff: boolean;

  // ── Statut utilisateur
  localStatus: UserStatus;
  awayReason: 'manual' | 'tab-hidden' | null;

  // ── Modération (visuel)
  mutedPlayerIds: Set<string>;

  // ── Screen share
  isScreenSharing: boolean;

  // ── Wave notifications
  waveNotifications: WaveNotification[];

  // ── Porte à proximité
  nearbyDoor: { doorId: string; doorName: string; linkedBubble: string; locked: boolean } | null;

  // ── Zone area (iframe overlay / launch prompt)
  activeArea: { name: string; url: string; label: string; action?: 'iframe' | 'launch' } | null;
  iframeDismissed: boolean;

  // ── Zoom carte (5 niveaux)
  zoomLevel: number;

  // ── Verrouillage salles
  lockedBubbles: Set<string>;
  lockedBubbleOwners: Map<string, Set<string>>;
  knockRequests: Array<{ bubbleId: string; userId: string; displayName: string }>;

  /** mapZoneId des meeting-rooms en cours d'enregistrement — bandeau RGPD
   *  permanent (spec design 2026-08-20), alimenté par recording:changed
   *  (Colyseus → apps/api via Redis, voir ZoneRoom.ts). */
  recordingBubbles: Set<string>;
  /** Début (ms epoch) de l'enregistrement de chaque salle, quand il est connu : durée affichée. */
  recordingStartedAt: Map<string, number>;

  // ── Transition inter-zones
  /** Slug de la zone à charger au prochain BootScene (ex: 'lobby', 'meetingroom') */
  targetZoneSlug: string;
  /** Nom du spawn point dans le TMJ cible (ex: 'from_main'). null = centre de la map. */
  targetSpawnName: string | null;
  /**
   * Dernière position connue du joueur avant destruction de la connexion
   * Colyseus (ex: aller-retour panneau admin) — prioritaire sur targetSpawnName
   * si zoneSlug correspond à la zone rechargée. Consommé une seule fois.
   */
  lastPlayerPosition: { zoneSlug: string; x: number; y: number } | null;

  // ── Bureau personnel (Sprint 11a)
  deskAssignments: Array<{ deskId: string; userId: string; displayName: string }>;
  localDeskId: string | null;
  nearbyDesk: { deskId: string; label: string; ownerId: string | null; ownerName: string | null } | null;
  /** Bureaux personnels — état affiché par PersonalDeskPrompt, tenu par game/systems/PersonalDesks.ts. */
  personalDeskUi: PersonalDeskUi;

  // ── Présence par zone (Sprint 11b)
  allZonePresence: Array<{ zoneSlug: string; zoneName: string; players: Array<{ userId: string; displayName: string; status: string; sheet?: string }> }>;
  presenceViewMode: 'list' | 'zones';

  // ── Podium (Sprint 11d)
  podiumSpeakerId: string | null;
  podiumSpeakerName: string | null;
  isInPodiumZone: boolean;
  raisedHands: Array<{ userId: string; displayName: string }>;

  // ── Admin panel
  adminPage: 'members' | 'stats' | 'map' | 'recordings' | 'desks' | 'feedback' | null;

  // ── Parametres utilisateur (localStorage) ─────────────────
  settings: {
    audioEnabled: boolean;
    videoEnabled: boolean;
    soundsEnabled: boolean;
    toastsEnabled: boolean;
    reduceMotion: boolean;
    defaultZoom: number;
    selectedMicId: string;
    selectedCameraId: string;
    selectedSpeakerId: string;
    /** Thème de l'interface — appliqué par apps/web (ds/theme.ts). */
    theme: 'dark' | 'light' | 'system' | 'campus';
    /** Lumière d'ambiance sur le campus (apps/web, systems/AmbientLight.ts) — touche L. */
    ambientLight: boolean;
    /**
     * Arrière-plan de sa caméra (apps/web, services/videoBackground.ts) :
     * 'none', 'blur-light', 'blur', 'photo:{id}' ou 'custom'.
     */
    videoBackground: string;
  };
}

export interface GameActions {
  setCurrentZone:       (zoneId: string, zoneName: string) => void;
  clearCurrentZone:     () => void;
  setTransitioning:     (v: boolean) => void;
  addNearbyPlayer:      (p: NearbyPlayer) => void;
  removeNearbyPlayer:   (userId: string) => void;
  setZonePlayers:       (players: Map<string, { userId: string; displayName: string; status: string }>) => void;
  setProximityCount:    (n: number) => void;
  setLocalBubbleId:     (id: string | null) => void;
  setLocalBubbleType:   (type: string | null) => void;
  setBubbleNames:       (names: Map<string, string>) => void;
  setLocalBubbleHasDoor: (v: boolean) => void;
  setPlayerBubbleIds:   (ids: Map<string, string | null>) => void;
  setLiveKitConnected:  (v: boolean) => void;
  setRealtimeStatus:    (v: RealtimeStatus) => void;
  setActiveSpeakers:    (ids: string[]) => void;
  setLocalMuted:        (v: boolean) => void;
  setBroadcastActive:   (v: boolean) => void;
  setMutedUntil:        (ts: number | null) => void;
  setProximityUserIds:  (ids: string[]) => void;
  addProximityMessage:  (msg: ProximityMessage) => void;
  clearProximityMessages: () => void;
  setProximityMessages: (msgs: ProximityMessage[]) => void;
  openModal:            (modal: NonNullable<ModalId>) => void;
  closeModal:           () => void;
  setJwt:               (jwt: string) => void;
  setAuthState:         (s: AuthState) => void;
  setProfile:           (displayName: string, avatarIndex: number) => void;
  setAppearance:        (appearance: AvatarAppearance) => void;
  setLocalCameraOff:    (v: boolean) => void;
  setTargetZoneSlug:    (slug: string) => void;
  setTargetSpawnName:   (name: string | null) => void;
  setLastPlayerPosition: (pos: { zoneSlug: string; x: number; y: number } | null) => void;
  setLocalStatus:       (status: UserStatus) => void;
  setAwayReason:        (reason: 'manual' | 'tab-hidden' | null) => void;
  addMutedPlayerId:     (userId: string) => void;
  removeMutedPlayerId:  (userId: string) => void;
  setScreenSharing:     (v: boolean) => void;
  addWaveNotification:    (w: WaveNotification) => void;
  removeWaveNotification: (fromUserId: string) => void;
  setNearbyDoor:          (door: { doorId: string; doorName: string; linkedBubble: string; locked: boolean } | null) => void;
  setActiveArea:          (area: { name: string; url: string; label: string; action?: 'iframe' | 'launch' } | null) => void;
  setIframeDismissed:     (v: boolean) => void;
  setZoomLevel:           (level: number) => void;
  addLockedBubble:        (bubbleId: string) => void;
  removeLockedBubble:     (bubbleId: string) => void;
  setLockedBubbleOwner:   (bubbleId: string, userId: string) => void;
  removeLockedBubbleOwner: (bubbleId: string) => void;
  /** Remplace la liste de ceux qui ont fermé la salle (état complet du serveur). */
  setLockedBubbleOwners: (bubbleId: string, userIds: string[]) => void;
  addKnockRequest:        (r: { bubbleId: string; userId: string; displayName: string }) => void;
  removeKnockRequest:     (userId: string) => void;
  setRecordingActive:     (mapZoneId: string, active: boolean, startedAt?: number) => void;
  setDeskAssignments:     (a: Array<{ deskId: string; userId: string; displayName: string }>) => void;
  setLocalDeskId:         (id: string | null) => void;
  setNearbyDesk:          (d: { deskId: string; label: string; ownerId: string | null; ownerName: string | null } | null) => void;
  setPersonalDeskUi:      (patch: Partial<PersonalDeskUi>) => void;
  setAllZonePresence:     (p: Array<{ zoneSlug: string; zoneName: string; players: Array<{ userId: string; displayName: string; status: string; sheet?: string }> }>) => void;
  setPresenceViewMode:    (m: 'list' | 'zones') => void;
  setPodiumSpeaker:       (id: string | null, name: string | null) => void;
  setInPodiumZone:        (v: boolean) => void;
  addRaisedHand:          (h: { userId: string; displayName: string }) => void;
  removeRaisedHand:       (userId: string) => void;
  clearRaisedHands:       () => void;
  updateSettings:         (partial: Partial<GameState['settings']>) => void;
  setAdminPage:           (page: GameState['adminPage']) => void;
}

export type GameStore = GameState & GameActions;

// 300 : historique du chat d'une salle de réunion (apps/realtime/src/lib/meetingChat.ts).
const MAX_PROXIMITY_MESSAGES = 300;

function loadSettings(): GameState['settings'] {
  try {
    const raw = localStorage.getItem('campus:settings');
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<GameState['settings']> & { backgroundBlur?: boolean };
      // Ancien réglage (interrupteur de flou, avant les décors) : repris tel quel.
      if (parsed.videoBackground === undefined && parsed.backgroundBlur) parsed.videoBackground = 'blur';
      delete parsed.backgroundBlur;
      return { ...DEFAULT_SETTINGS, ...parsed };
    }
  } catch { /* corrupt data — use defaults */ }
  return { ...DEFAULT_SETTINGS };
}

const DEFAULT_SETTINGS: GameState['settings'] = {
  audioEnabled: true,
  videoEnabled: false,
  soundsEnabled: true,
  toastsEnabled: true,
  reduceMotion: false,
  defaultZoom: 1,
  selectedMicId: '',
  selectedCameraId: '',
  selectedSpeakerId: '',
  theme: 'dark',
  ambientLight: false,
  videoBackground: 'none',
};

export const useGameStore = create<GameStore>()(subscribeWithSelector((set) => ({
  currentZoneId:     null,
  currentZoneName:   null,
  isTransitioning:   false,
  nearbyPlayers:     new Map(),
  zonePlayers:       new Map(),
  proximityCount:    0,
  localBubbleId:     null,
  localBubbleType:   null,
  bubbleNames:       new Map(),
  localBubbleHasDoor: false,
  playerBubbleIds:   new Map(),
  liveKitConnected:  false,
  realtimeStatus:    'connected',
  activeSpeakers:    [],
  isLocalMuted:      false,
  isBroadcastActive: false,
  mutedUntil:        null,
  proximityUserIds:  [],
  proximityMessages: [],
  activeModal:       null,
  jwt:               null,
  authState:         'unauthenticated',
  displayName:       null,
  avatarIndex:       0,
  appearance:        null,
  isLocalCameraOff:  true,
  localStatus:       'available' as UserStatus,
  awayReason:        null,
  mutedPlayerIds:    new Set<string>(),
  isScreenSharing:   false,
  waveNotifications: [],
  nearbyDoor:        null,
  activeArea:        null,
  iframeDismissed:   false,
  // Vide = carte d'arrivée du campus (BootScene.resolveZoneSlug).
  targetZoneSlug:    '',
  targetSpawnName:   null,
  lastPlayerPosition: null,
  deskAssignments: [],
  localDeskId: null,
  nearbyDesk: null,
  personalDeskUi: { area: null, desk: null, slot: null, areaHasFreeSlot: false, placing: null, myDesk: null, customizing: null },
  allZonePresence: [],
  presenceViewMode: 'list' as const,
  podiumSpeakerId: null,
  podiumSpeakerName: null,
  isInPodiumZone: false,
  raisedHands: [],
  adminPage: null,

  setCurrentZone: (zoneId, zoneName) =>
    set({ currentZoneId: zoneId, currentZoneName: zoneName, isTransitioning: false }),
  clearCurrentZone: () =>
    set({ currentZoneId: null, currentZoneName: null }),
  setTransitioning: (v) => set({ isTransitioning: v }),

  addNearbyPlayer: (p) =>
    set((s) => { const m = new Map(s.nearbyPlayers); m.set(p.userId, p); return { nearbyPlayers: m }; }),
  removeNearbyPlayer: (userId) =>
    set((s) => { const m = new Map(s.nearbyPlayers); m.delete(userId); return { nearbyPlayers: m }; }),
  setZonePlayers:    (players) => set({
    zonePlayers: new Map([...players.entries()].map(([k, v]) => [k, { ...v, status: (v.status || 'available') as UserStatus }])),
  }),
  setProximityCount: (n) => set({ proximityCount: n }),

  setLocalBubbleId:   (id) => set({ localBubbleId: id }),
  setLocalBubbleType: (type) => set({ localBubbleType: type }),
  setBubbleNames:     (names) => set({ bubbleNames: names }),
  setLocalBubbleHasDoor: (v) => set({ localBubbleHasDoor: v }),
  setPlayerBubbleIds: (ids) => set({ playerBubbleIds: ids }),

  setLiveKitConnected: (v) => set({ liveKitConnected: v }),
  setRealtimeStatus:   (v) => set({ realtimeStatus: v }),
  setActiveSpeakers:   (ids) => set({ activeSpeakers: ids }),
  setLocalMuted:       (v) => set({ isLocalMuted: v }),
  setBroadcastActive:  (v) => set({ isBroadcastActive: v }),
  setMutedUntil:       (ts) => set({ mutedUntil: ts }),

  setProximityUserIds: (ids) => set({ proximityUserIds: ids }),

  addProximityMessage: (msg) =>
    set((s) => ({
      proximityMessages: [...s.proximityMessages, msg].slice(-MAX_PROXIMITY_MESSAGES),
    })),
  clearProximityMessages: () => set({ proximityMessages: [] }),
  setProximityMessages: (msgs) => set({ proximityMessages: msgs.slice(-MAX_PROXIMITY_MESSAGES) }),

  openModal:  (modal) => set({ activeModal: modal }),
  closeModal: () => set({ activeModal: null }),
  setJwt:     (jwt) => set({ jwt }),
  setAuthState: (s) => set({ authState: s }),
  setProfile: (displayName, avatarIndex) => set({ displayName, avatarIndex }),
  setAppearance: (appearance) => set({ appearance }),
  setLocalCameraOff: (v) => set({ isLocalCameraOff: v }),
  setTargetZoneSlug: (slug) => set({ targetZoneSlug: slug }),
  setTargetSpawnName: (name) => set({ targetSpawnName: name }),
  setLastPlayerPosition: (pos) => set({ lastPlayerPosition: pos }),
  setDeskAssignments: (a) => set({ deskAssignments: a }),
  setLocalDeskId: (id) => set({ localDeskId: id }),
  setNearbyDesk: (d) => set({ nearbyDesk: d }),
  setPersonalDeskUi: (patch) => set((s) => ({ personalDeskUi: { ...s.personalDeskUi, ...patch } })),
  setAllZonePresence: (p) => set({ allZonePresence: p }),
  setPresenceViewMode: (m) => set({ presenceViewMode: m }),
  setPodiumSpeaker: (id, name) => set({ podiumSpeakerId: id, podiumSpeakerName: name }),
  setInPodiumZone: (v) => set({ isInPodiumZone: v }),
  addRaisedHand: (h) =>
    set((s) => ({ raisedHands: s.raisedHands.some((r) => r.userId === h.userId) ? s.raisedHands : [...s.raisedHands, h] })),
  removeRaisedHand: (userId) =>
    set((s) => ({ raisedHands: s.raisedHands.filter((r) => r.userId !== userId) })),
  clearRaisedHands: () => set({ raisedHands: [] }),
  setLocalStatus: (status) => set({ localStatus: status }),
  setAwayReason: (reason) => set({ awayReason: reason }),
  addMutedPlayerId: (userId) =>
    set((s) => { const m = new Set(s.mutedPlayerIds); m.add(userId); return { mutedPlayerIds: m }; }),
  removeMutedPlayerId: (userId) =>
    set((s) => { const m = new Set(s.mutedPlayerIds); m.delete(userId); return { mutedPlayerIds: m }; }),
  setScreenSharing: (v) => set({ isScreenSharing: v }),
  addWaveNotification: (w) =>
    set((s) => ({ waveNotifications: [...s.waveNotifications, w] })),
  removeWaveNotification: (fromUserId) =>
    set((s) => ({ waveNotifications: s.waveNotifications.filter((n) => n.fromUserId !== fromUserId) })),
  setNearbyDoor: (door) => set({ nearbyDoor: door }),
  setActiveArea: (area) => set({ activeArea: area }),
  setIframeDismissed: (v) => set({ iframeDismissed: v }),
  zoomLevel: parseFloat(localStorage.getItem('campus:zoomLevel') ?? '1'),
  setZoomLevel: (level) => {
    localStorage.setItem('campus:zoomLevel', String(level));
    set({ zoomLevel: level });
  },
  lockedBubbles: new Set(),
  lockedBubbleOwners: new Map(),
  knockRequests: [],
  recordingBubbles: new Set(),
  recordingStartedAt: new Map(),
  addLockedBubble: (bubbleId) =>
    set((s) => { const m = new Set(s.lockedBubbles); m.add(bubbleId); return { lockedBubbles: m }; }),
  removeLockedBubble: (bubbleId) =>
    set((s) => { const m = new Set(s.lockedBubbles); m.delete(bubbleId); return { lockedBubbles: m }; }),
  setLockedBubbleOwner: (bubbleId, userId) =>
    set((s) => {
      const m = new Map(s.lockedBubbleOwners);
      const owners = new Set(m.get(bubbleId));
      owners.add(userId);
      m.set(bubbleId, owners);
      return { lockedBubbleOwners: m };
    }),
  removeLockedBubbleOwner: (bubbleId) =>
    set((s) => { const m = new Map(s.lockedBubbleOwners); m.delete(bubbleId); return { lockedBubbleOwners: m }; }),
  setLockedBubbleOwners: (bubbleId, userIds) =>
    set((s) => { const m = new Map(s.lockedBubbleOwners); m.set(bubbleId, new Set(userIds)); return { lockedBubbleOwners: m }; }),
  addKnockRequest: (r) =>
    set((s) => ({ knockRequests: [...s.knockRequests, r] })),
  removeKnockRequest: (userId) =>
    set((s) => ({ knockRequests: s.knockRequests.filter((k) => k.userId !== userId) })),
  setRecordingActive: (mapZoneId, active, startedAt) =>
    set((s) => {
      const m = new Set(s.recordingBubbles);
      const t = new Map(s.recordingStartedAt);
      if (active) m.add(mapZoneId); else m.delete(mapZoneId);
      if (active && startedAt) t.set(mapZoneId, startedAt); else if (!active) t.delete(mapZoneId);
      return { recordingBubbles: m, recordingStartedAt: t };
    }),
  settings: loadSettings(),
  updateSettings: (partial) =>
    set((s) => {
      const next = { ...s.settings, ...partial };
      localStorage.setItem('campus:settings', JSON.stringify(next));
      return { settings: next };
    }),
  setAdminPage: (page) => set({ adminPage: page }),
})));
